// src/components/AssessmentImportPane.jsx
// 小テスト・課題・感想の取り込み（出席記録ページの一部）【2026-09-11】
//
// manaba のエクスポート（query-… / score-… / report-….xlsx）を、この画面に
// ドラッグ＆ドロップすると取り込める。画面のどこに落としてもよい。
//
// 【一括取り込みの方式】
//   1枚ずつモーダルで確認させると、10枚落としたときに10回モーダルが出て、
//   途中でどれを確認済みか分からなくなる。そこで落とした全ファイルを
//   「取り込みリスト」に並べ、行ごとに種類を選び、まとめて確定する形にした。
//     - 解析（preview）は落とした時点で自動。保存はしない
//     - 種類は自動判定した値が初期値。行のプルダウンで上書きできる
//     - 行ごとに「除外」できる。まとめて取り込むのは残った行だけ
//   種類を変えても読み取り方は変わらない（点数も感想もファイルの形で読む）。
//   変わるのは「どの集計に入るか」だけ。
//
// 判別と突合の中身は backend/src/utils/manabaParser.js 側にある。

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import SaveConsent from './SaveConsent';
import { PageDropOverlay } from './PageFileDrop';
import { usePeriod } from './PeriodBar';
import { confirmDialog } from './ConfirmDialog';
import { useImportSources } from '../hooks/useUniversitySettings';

const KIND_OPTIONS = [
  { value: 'quiz',         label: '小テスト',       bg: '#ede9fe', fg: '#6d28d9' },
  { value: 'survey',       label: '感想・発言点',   bg: '#d1fae5', fg: '#047857' },
  { value: 'report',       label: '課題・レポート', bg: '#ffedd5', fg: '#c2410c' },
  { value: 'course_total', label: 'コース成績',     bg: '#e0e7ff', fg: '#3730a3' },
  { value: 'attendance',   label: '出席記録CSV',    bg: '#e0f2fe', fg: '#0369a1' },
];
const kindInfo = (v) =>
  KIND_OPTIONS.find(k => k.value === v) || { value: v, label: v, bg: '#f3f4f6', fg: '#374151' };

/**
 * その行で選べる種類。
 *   解析できなかった csv → 出席記録CSV しか選びようがない
 *   xlsx                → 出席記録CSV は選ばせない（サーバーが csv しか受けない）
 */
function optionsFor(item) {
  const isCsv = /\.(csv|tsv)$/i.test(item.file.name);
  if (!item.preview) return KIND_OPTIONS.filter(k => k.value === 'attendance');
  return KIND_OPTIONS.filter(k => k.value !== 'attendance' || isCsv);
}

const authHeader = () => ({ Authorization: 'Bearer ' + localStorage.getItem('token') });

/** マイグレーション未適用だと PG のエラーがそのまま来るので、読める言葉に直す */
function humanizeError(msg) {
  const m = String(msg || '');
  if (/relation .*assessment.* does not exist|assessments.*存在しません/i.test(m)) {
    return 'サーバー側のテーブルがまだ作られていません（v36 のマイグレーション未適用）。'
         + ' migration_v35_to_v36_assessments.sql を流してください。';
  }
  return m;
}

/**
 * ドロップされたものを再帰的に歩いて、ファイルと相対パスを集める【2026-09-11】
 *
 * 年度/学年/前期後期/授業名 のように、几帳面に子フォルダで整理している人が
 * フォルダごと落とせるようにする。DataTransferItem は await をまたぐと
 * 使えなくなるので、entry の取り出しだけは同期でやること。
 */
async function walkEntry(entry, prefix, out) {
  if (!entry) return;
  if (entry.isFile) {
    const file = await new Promise((res, rej) => entry.file(res, rej));
    out.push({ file, relPath: prefix + entry.name });
    return;
  }
  if (entry.isDirectory) {
    const reader = entry.createReader();
    // readEntries は一度に全部返さない。空が返るまで呼び続ける
    for (;;) {
      const batch = await new Promise((res, rej) => reader.readEntries(res, rej));
      if (!batch || batch.length === 0) break;
      for (const child of batch) await walkEntry(child, prefix + entry.name + '/', out);
    }
  }
}

/** 相対パスからフォルダ部分を取り出す（'' なら直下） */
const dirOf = (relPath) => {
  const i = String(relPath || '').lastIndexOf('/');
  return i < 0 ? '' : relPath.slice(0, i);
};

/**
 * フォルダ名から取り込み先の授業を当てる。
 * 「2026/2年/前期/経営学/query-….xlsx」の授業名の部分を拾う想定で、
 * パスの後ろ側から順に授業名と突き合わせる（深い方が具体的なので）。
 * 当てにいくのはあくまで初期値。最終的には行のプルダウンで決める。
 */
function guessClass(relPath, classes) {
  const norm = (v) => String(v || '').normalize('NFKC').trim().toLowerCase();
  const segs = String(relPath || '').split('/').slice(0, -1).map(norm).filter(Boolean);
  for (let i = segs.length - 1; i >= 0; i--) {
    const seg = segs[i];
    const hit = classes.find(c => {
      const n = norm(c.name);
      return n && (n === seg || seg.includes(n) || n.includes(seg));
    });
    if (hit) return hit;
  }
  return null;
}

let seq = 0;

// onAccept   … ファイルを受け取ったとき（出席記録の全画面で、取り込みの引き出しを開くため）【2026-10-13】
// onImported … 取り込みが終わったとき（集計を読み直すため）【2026-10-13】
export default function AssessmentImportPane({ classId, className, setErr, setMsg, onAccept, onImported }) {
  const onAcceptRef = useRef(onAccept);
  onAcceptRef.current = onAccept;
  const [dragging, setDragging] = useState(false);
  const [batch, setBatch]       = useState([]);   // 取り込みリスト
  const [running, setRunning]   = useState(false);
  const [list, setList]         = useState([]);   // 取り込み済みの課題
  // サーバーに保存してよいかの確認【2026-09-11】。名簿以外は既定オフ。
  // 感想の本文まで残るので、黙って保存してよいものではない。
  const [saveOk, setSaveOk]     = useState(false);
  // 取り込み先の候補。フォルダ名から授業を当てるのに使う【2026-09-11】
  const [allClassList, setClassList] = useState([]);
  // 行き先の授業は、画面で見ている期間の授業だけ（フォルダ名からの推測もこの中から）【2026-09-17】
  const period = usePeriod();
  const classList = useMemo(() => period.filter(allClassList), [period.filter, allClassList]); // eslint-disable-line react-hooks/exhaustive-deps
  // 落としたファイルを1枚の表にまとめて見る【2026-09-11】
  //   見本（excel-converter の「出席×小テスト×提出物」）と同じ発想。
  //   サーバーには何も保存せず、解析結果だけをブラウザの中で突き合わせる。
  //   保存をオフにしたまま確認だけしたい、という使い方ができる。
  const [combined, setCombined] = useState(false);
  const [listBusy, setListBusy] = useState(true);
  const gradesOn = useImportSources().on('manaba_grades');   // 大学で使わない設定なら取り込み欄を出さない【2026-11-04】
  const dragDepth = useRef(0);
  const inputRef  = useRef(null);
  const dirInputRef = useRef(null);
  // まとめて取り込むとき、行の種類はユーザーが直しているかもしれない。
  // ループの中から最新の state を読むために ref に写しておく。
  const batchRef  = useRef([]);
  useEffect(() => { batchRef.current = batch; }, [batch]);

  const patch = (id, fields) =>
    setBatch(b => b.map(it => (it.id === id ? { ...it, ...fields } : it)));

  // ---- 取り込み済みの一覧 -------------------------------------------------
  const loadList = useCallback(async () => {
    setListBusy(true);
    try {
      const res = await fetch('/api/assessments?classId=' + classId, { headers: authHeader() });
      const d = await res.json();
      if (!res.ok || !d.success) throw new Error(d.message || ('HTTP ' + res.status));
      setList(d.assessments || []);
    } catch (e) {
      // 一覧が取れないだけで取り込み自体は使えるので、画面は赤くしない
      setList([]);
      // eslint-disable-next-line no-console
      console.warn('課題一覧の取得に失敗:', e.message);
    } finally { setListBusy(false); }
  }, [classId]);

  useEffect(() => { loadList(); }, [loadList]);

  useEffect(() => {
    if (!period.ready) return;
    fetch('/api/schedule/classes' + (period.year ? '?academicYear=' + period.year : ''), { headers: authHeader() })
      .then(r => r.json())
      .then(d => setClassList(Array.from(new Map((d.classes || d.data || []).map(c => [c.id, c])).values())))
      .catch(() => {});
  }, [period.ready, period.year]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- 解析（保存しない） -------------------------------------------------
  const parseOne = useCallback(async (item) => {
    patch(item.id, { status: 'parsing', error: '' });
    try {
      const fd = new FormData();
      fd.append('file', item.file);
      fd.append('classId', item.classId || classId);
      // 全行もらう。保存せずに複数ファイルを1枚にまとめて見るため【2026-09-11】
      fd.append('limit', 'all');
      const res = await fetch('/api/assessments/import/preview', {
        method: 'POST', headers: authHeader(), body: fd
      });
      const d = await res.json();
      if (!res.ok || !d.success) throw new Error(d.message || ('HTTP ' + res.status));
      patch(item.id, { status: 'ready', preview: d, kind: d.meta.kind });
    } catch (e) {
      const msg = humanizeError(e.message);
      // manaba形式でない csv は、出席記録CSVとして取り込める見込みがある。
      // 画面全体でファイルを受ける以上、カードリーダーのCSVは必ず落とされる。
      if (/\.csv$/i.test(item.file.name) && /判別できません/.test(msg)) {
        patch(item.id, { status: 'ready', preview: null, kind: 'attendance', error: '' });
      } else {
        patch(item.id, { status: 'error', error: msg });
      }
    }
  }, [classId]);

  // ---- 取り込み（確定） ---------------------------------------------------
  const importOne = async (item) => {
    patch(item.id, { status: 'importing', error: '' });
    try {
      const fd = new FormData();
      fd.append('file', item.file);
      fd.append('classId', item.classId || classId);

      if (item.kind === 'attendance') {
        const res = await fetch('/api/csv-import/attendance', {
          method: 'POST', headers: authHeader(), body: fd
        });
        const d = await res.json();
        if (!res.ok || !d.success) throw new Error(d.message || ('HTTP ' + res.status));
        patch(item.id, {
          status: 'done',
          result: { title: '出席記録CSV', matched: d.successCount ?? 0, unmatched: d.errorCount ?? 0 }
        });
        return true;
      }

      const p = item.preview;
      fd.append('kind', item.kind);
      if (p) {
        fd.append('title', p.meta.title);
        if (p.meta.assignedDate) fd.append('assignedDate', p.meta.assignedDate);
      }
      const res = await fetch('/api/assessments/import', {
        method: 'POST', headers: authHeader(), body: fd
      });
      const d = await res.json();
      if (!res.ok || !d.success) throw new Error(d.message || ('HTTP ' + res.status));
      patch(item.id, { status: 'done', result: d.result });
      return true;
    } catch (e) {
      patch(item.id, { status: 'error', error: humanizeError(e.message) });
      return false;
    }
  };

  /** まとめて取り込む。除外・エラー・取り込み済みは飛ばす */
  const importAll = async () => {
    if (!saveOk) { setErr('サーバーに保存することを確認してください'); return; }
    let targetIds = batchRef.current.filter(it => it.status === 'ready').map(it => it.id);
    // 前に同じ名前のファイルを取り込んでいるもの（先生ごと）だけ、更新してよいか聞く【2026-10-01】
    //   初めてのファイルは聞かずにそのまま取り込む
    const again = batchRef.current.filter(it => targetIds.includes(it.id)
      && it.preview && (it.preview.sameFileImports || []).length > 0);
    if (again.length > 0) {
      const lines = again.slice(0, 10).map(it => {
        const last = it.preview.sameFileImports[0];
        const when = last.importedAt ? new Date(last.importedAt).toLocaleDateString('ja-JP') : '';
        return `・${it.file.name}（${when}「${last.title}」）`;
      });
      const yes = (await confirmDialog(
        `次の ${again.length} 件は、前に同じ名前のファイルを取り込んでいます。\n` +
        lines.join('\n') + (again.length > 10 ? '\n…ほか' : '') +
        '\n\n更新しますか？\n［OK］更新する　［キャンセル］これらは飛ばして、初めてのファイルだけ取り込む'
      ));
      if (!yes) {
        const skip = new Set(again.map(it => it.id));
        targetIds = targetIds.filter(id => !skip.has(id));
        if (targetIds.length === 0) { setMsg('取り込みをやめました（すべて取り込み済みのファイルでした）'); return; }
      }
    }
    setRunning(true); setErr(''); setMsg('');
    let ok = 0, ng = 0;
    for (const id of targetIds) {
      const fresh = batchRef.current.find(x => x.id === id);
      if (!fresh || fresh.status !== 'ready') continue;   // 途中で除外されたら飛ばす
      const done = await importOne(fresh);
      if (done) ok++; else ng++;
    }
    await loadList();
    setRunning(false);
    if (ok && onImported) onImported();
    setSaveOk(false);   // 次の取り込みでも改めて確認してもらう
    if (ok)  setMsg(ok + ' 件を取り込みました' + (ng ? '（' + ng + ' 件は失敗）' : ''));
    if (!ok && ng) setErr('取り込みに失敗しました。各行のエラーを確認してください');
  };

  // ---- ファイルを受け取る -------------------------------------------------
  /**
   * @param {Array<{file: File, relPath: string}>} entries
   */
  const accept = useCallback((entries) => {
    const files = (entries || []).filter(e => e.file && /\.(xlsx|xlsm|csv|tsv)$/i.test(e.file.name));
    if (files.length === 0) {
      setErr('xlsx か csv のファイルを落としてください（フォルダごとでも可）');
      return;
    }
    const items = files.map(e => {
      const hit = guessClass(e.relPath, classList);
      return {
        id: ++seq, file: e.file,
        relPath: e.relPath || e.file.name,
        dir: dirOf(e.relPath || ''),
        // フォルダ名で当たればその授業、当たらなければ今開いている授業
        classId: hit ? String(hit.id) : String(classId),
        guessedFrom: hit ? hit.name : null,
        status: 'pending', preview: null,
        kind: null, error: '', expanded: false, result: null,
      };
    });
    // フォルダ順 → ファイル名順に並べる。几帳面に切ったフォルダの順で見える方が探しやすい
    items.sort((a, b) => a.dir.localeCompare(b.dir, 'ja') || a.relPath.localeCompare(b.relPath, 'ja'));
    setBatch(b => [...b, ...items]);
    if (onAcceptRef.current) onAcceptRef.current(items.length);
    // 解析は1件ずつ。まとめて投げるとサーバー側の一時ファイルが膨らむ
    (async () => { for (const it of items) await parseOne(it); })();
  }, [parseOne, setErr, classList, classId]);

  // ---- 画面のどこに落としてもよいようにする -------------------------------
  useEffect(() => {
    const hasFiles = (e) =>
      e.dataTransfer && Array.from(e.dataTransfer.types || []).includes('Files');
    const onEnter = (e) => { if (!hasFiles(e)) return; dragDepth.current += 1; setDragging(true); };
    const onOver  = (e) => { if (hasFiles(e)) e.preventDefault(); };
    const onLeave = (e) => {
      if (!hasFiles(e)) return;
      dragDepth.current = Math.max(0, dragDepth.current - 1);
      if (dragDepth.current === 0) setDragging(false);
    };
    const onDrop = (e) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      dragDepth.current = 0; setDragging(false);

      // entry の取り出しは同期でやる。await をまたぐと DataTransfer が使えなくなる
      const items = Array.from(e.dataTransfer.items || []);
      const entries = items
        .map(i => (typeof i.webkitGetAsEntry === 'function' ? i.webkitGetAsEntry() : null))
        .filter(Boolean);

      if (entries.length > 0) {
        (async () => {
          const out = [];
          for (const en of entries) await walkEntry(en, '', out);
          accept(out);
        })();
      } else {
        // webkitGetAsEntry が無いブラウザ向け。フォルダは拾えない
        accept(Array.from(e.dataTransfer.files || []).map(f => ({ file: f, relPath: f.name })));
      }
    };
    window.addEventListener('dragenter', onEnter);
    window.addEventListener('dragover',  onOver);
    window.addEventListener('dragleave', onLeave);
    window.addEventListener('drop',      onDrop);
    return () => {
      window.removeEventListener('dragenter', onEnter);
      window.removeEventListener('dragover',  onOver);
      window.removeEventListener('dragleave', onLeave);
      window.removeEventListener('drop',      onDrop);
    };
  }, [accept]);

  /**
   * 落としたファイルを1枚の表に突き合わせる【2026-09-11】
   * 学籍番号をキーに、ファイルを列として横に並べる。
   * サーバーには何も送らない。解析済みのデータをブラウザの中で結合するだけ。
   */
  const cross = useMemo(() => {
    const cols = batch.filter(it => it.preview && it.status !== 'skipped');
    const map = new Map();
    for (const it of cols) {
      for (const r of it.preview.rows) {
        const id = r.rawStudentId;
        if (!map.has(id)) {
          map.set(id, { id, name: r.matchedName || r.rawName || '', matched: !!r.matchedUserId, cells: {} });
        }
        const row = map.get(id);
        if (!row.name) row.name = r.matchedName || r.rawName || '';
        if (r.matchedUserId) row.matched = true;   // 1つでも突合できれば名簿にいる
        row.cells[it.id] = r;
      }
    }
    const rows = Array.from(map.values()).sort((a, b) => a.id.localeCompare(b.id));
    for (const row of rows) {
      let scoreSum = 0, scoreMax = 0, refCount = 0, speakSum = 0, subCount = 0, hasScore = false;
      for (const it of cols) {
        const c = row.cells[it.id];
        if (!c) continue;
        if (it.kind === 'quiz') {
          if (c.score != null) { scoreSum += Number(c.score); hasScore = true; }
          const m = it.preview.meta.maxScore;
          if (m != null) scoreMax += Number(m);
        }
        if (c.reflectionLen) refCount++;
        if (c.speakCount != null) speakSum += Number(c.speakCount);
        if (c.dbStatus && c.dbStatus !== 'unsubmitted') subCount++;
      }
      row.total = { scoreSum, scoreMax, refCount, speakSum, subCount, hasScore };
    }
    return { cols, rows };
  }, [batch]);

  /** 1マスの見せ方。種類で意味が違うので、数字だけ出して詳しくは title に入れる */
  const crossCell = (it, c) => {
    if (!c) return { text: '', title: 'このファイルには載っていない学生' };
    const unsub = c.dbStatus === 'unsubmitted';
    if (it.kind === 'quiz') {
      return {
        text: c.score != null ? String(c.score) : (unsub ? '未' : '—'),
        title: c.rawStudentId + '　' + c.status
             + (c.score != null ? '　得点 ' + c.score : '')
             + (c.submittedAt ? '　' + c.submittedAt.slice(0, 16).replace('T', ' ') : ''),
        dim: unsub,
      };
    }
    if (it.kind === 'survey') {
      const parts = [];
      if (c.reflectionLen) parts.push(c.reflectionLen + '字');
      if (c.speakCount != null && c.speakCount > 0) parts.push('発' + c.speakCount);
      return {
        text: parts.length ? parts.join(' ') : (unsub ? '未' : '—'),
        title: c.rawStudentId + '　' + c.status
             + (c.reflectionLen ? '　感想 ' + c.reflectionLen + '字' : '')
             + (c.speakCount != null ? '　発言 ' + c.speakCount + '回' : ''),
        dim: unsub,
      };
    }
    return {
      text: unsub ? '未' : (c.submittedAt ? c.submittedAt.slice(5, 10) : '✓'),
      title: c.rawStudentId + '　' + c.status
           + (c.submittedAt ? '　' + c.submittedAt.slice(0, 16).replace('T', ' ') : ''),
      dim: unsub,
    };
  };

  /** 「確認待ち」の行が、どの授業に何件入るか */
  const targetBreakdown = useMemo(() => {
    const m = new Map();
    for (const it of batch) {
      if (it.status !== 'ready') continue;
      const id = String(it.classId || classId);
      const c = classList.find(x => String(x.id) === id);
      const name = c ? c.name : 'この授業';
      const cur = m.get(id) || { id, name, count: 0 };
      cur.count++; m.set(id, cur);
    }
    return [...m.values()].sort((a, b) => b.count - a.count);
  }, [batch, classList, classId]);

  const readyCount = batch.filter(it => it.status === 'ready').length;
  const doneCount  = batch.filter(it => it.status === 'done').length;

  const STATUS_TEXT = {
    pending:   ['待機中',   '#64748b'],
    parsing:   ['解析中…',  '#2563eb'],
    ready:     ['確認待ち', '#0f766e'],
    importing: ['取込中…',  '#2563eb'],
    done:      ['取込済み', '#16a34a'],
    error:     ['エラー',   '#dc2626'],
    skipped:   ['除外',     '#64748b'],
  };

  return (
    <>
      <div className="card">
        <div className="card-header"
             style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem' }}>
          <h2>小テスト・課題を取り込む</h2>
          {batch.length > 0 && (
            <button className="btn-secondary" disabled={running}
                    onClick={() => setBatch([])} style={{ whiteSpace: 'nowrap' }}>
              リストを消す
            </button>
          )}
        </div>
        {!gradesOn && (
          <p style={{ color: '#b45309', fontSize: '0.88rem' }}>
            この大学では、manaba の小テスト・課題の取り込みを使わない設定になっています（教務の「大学の設定」で変えられます）。取り込み済みのものは下に出ます。
          </p>
        )}

        <div
          onClick={() => inputRef.current && inputRef.current.click()}
          style={{
            ...(gradesOn ? {} : { display: 'none' }),
            border: '2px dashed ' + (dragging ? '#2563eb' : '#cbd5e1'),
            background: dragging ? '#eff6ff' : '#f9fafb',
            borderRadius: 10, padding: '1rem 1.2rem', textAlign: 'center',
            cursor: 'pointer', transition: 'background .15s, border-color .15s'
          }}
        >
          <div style={{ fontWeight: 600, color: '#334155' }}>
            manaba のエクスポートをドロップ（画面のどこでも可・<strong>フォルダごとでも可</strong>）
          </div>
          <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: '0.25rem' }}>
            query-….xlsx（小テスト・感想）／ report-….xlsx（課題）／ score-….xlsx（レスポン）
            ／ 出席記録CSV
          </div>
          <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: '0.25rem' }}>
            年度／学年／前期後期／授業名 のように子フォルダで整理してあれば、
            そのまま落とせます。フォルダ名から取り込み先の授業を推測します。
          </div>
          <div style={{ marginTop: '0.5rem' }}>
            <button type="button" className="btn-secondary"
                    onClick={e => { e.stopPropagation(); dirInputRef.current && dirInputRef.current.click(); }}
                    style={{ fontSize: '0.82rem' }}>
              フォルダを選ぶ…
            </button>
          </div>
          <input ref={inputRef} type="file" multiple
                 accept=".xlsx,.xlsm,.csv,.tsv" style={{ display: 'none' }}
                 onChange={e => {
                   accept(Array.from(e.target.files || []).map(f => ({ file: f, relPath: f.name })));
                   e.target.value = '';
                 }} />
          {/* フォルダを選ぶ口。webkitdirectory を付けると子フォルダごと拾える */}
          <input ref={dirInputRef} type="file" multiple webkitdirectory="" directory=""
                 style={{ display: 'none' }}
                 onChange={e => {
                   accept(Array.from(e.target.files || [])
                     .map(f => ({ file: f, relPath: f.webkitRelativePath || f.name })));
                   e.target.value = '';
                 }} />
        </div>

        {/* ---- 取り込みリスト ---- */}
        {batch.length > 0 && (
          <div style={{ marginTop: '1rem' }}>
            {/* 取り込み先の内訳【2026-09-11】
                フォルダごと落とすと複数の授業にまたがる。どこに何件入るのかは
                押す前に見えている必要がある。 */}
            {targetBreakdown.length > 0 && (
              <div style={{
                marginBottom: '0.6rem', fontSize: '0.86rem',
                background: targetBreakdown.length > 1 ? '#eff6ff' : '#f8fafc',
                border: '1px solid ' + (targetBreakdown.length > 1 ? '#bfdbfe' : '#e2e8f0'),
                borderRadius: 8, padding: '0.5rem 0.75rem', color: '#334155',
              }}>
                取り込み先：
                {targetBreakdown.map((t, i) => (
                  <span key={t.id} style={{ marginLeft: i === 0 ? '0.3rem' : '0.7rem' }}>
                    <strong>{t.name}</strong> {t.count}件
                  </span>
                ))}
                {targetBreakdown.length > 1 && (
                  <div style={{ color: '#1d4ed8', marginTop: '0.25rem' }}>
                    {targetBreakdown.length} つの授業に分かれて入ります。
                    違っていれば各行のプルダウンで直してください。
                  </div>
                )}
              </div>
            )}

            <SaveConsent
              checked={saveOk}
              onChange={setSaveOk}
              title="小テスト・課題の結果をサーバーに保存します"
              items={[
                '学籍番号・氏名（ファイルに書かれていたまま）',
                '得点・記号評価・提出の状態・提出日時・提出回数',
                '感想／自由記述の本文、発言回数、講評',
                '名簿と突合できなかった行も、そのまま残ります',
              ]}
              note="同じ課題を入れ直すと上書きされます。不要になったら課題ごと削除できます。"
              defaultsOn={false}
            />

            <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem',
                          flexWrap: 'wrap', marginBottom: '0.6rem' }}>
              <button className="btn-primary" disabled={running || readyCount === 0 || !saveOk}
                      onClick={importAll}>
                {running ? '取り込み中…' : '確認待ちの ' + readyCount + ' 件を取り込む'}
              </button>
              <span style={{ fontSize: '0.82rem', color: '#64748b' }}>
                種類は自動判定です。違っていれば行のプルダウンで変えてください。
                確定するまで保存されません。
              </span>
              {doneCount > 0 && (
                <span style={{ fontSize: '0.82rem', color: '#16a34a' }}>取込済み {doneCount} 件</span>
              )}
              {cross.cols.length > 0 && (
                <button className="btn-secondary" onClick={() => setCombined(v => !v)}>
                  {combined ? 'ファイル一覧に戻す' : 'まとめて見る（' + cross.cols.length + '枚）'}
                </button>
              )}
            </div>

            {!combined && (
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>ファイル</th>
                    <th style={{ minWidth: 150 }}>取り込む種類</th>
                    <th style={{ minWidth: 150 }}>取り込み先の授業</th>
                    <th>課題名</th>
                    <th style={{ whiteSpace: 'nowrap' }}>授業日</th>
                    <th style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>提出</th>
                    <th style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>突合できず</th>
                    <th style={{ whiteSpace: 'nowrap' }}>状態</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {batch.map((it, idx) => {
                    const p = it.preview;
                    const [stLabel, stColor] = STATUS_TEXT[it.status] || [it.status, '#374151'];
                    const dim = it.status === 'skipped' || it.status === 'done';
                    // フォルダが変わったら見出しを挟む。落としたフォルダの形のまま見える
                    const prevDir = idx > 0 ? batch[idx - 1].dir : null;
                    const showDir = it.dir !== prevDir;
                    return (
                      <React.Fragment key={it.id}>
                        {showDir && (
                          <tr>
                            <td colSpan={9} style={{ background: '#f1f5f9', fontWeight: 700,
                                                     fontSize: '0.82rem', color: '#334155' }}>
                              {it.dir
                                ? it.dir.split('/').join(' › ')
                                : <span style={{ color: '#64748b', fontWeight: 400 }}>（フォルダなし）</span>}
                            </td>
                          </tr>
                        )}
                        <tr style={dim ? { opacity: 0.6 } : undefined}>
                          <td style={{ maxWidth: 220, overflow: 'hidden',
                                       textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                              title={it.relPath || it.file.name}>
                            {it.file.name}
                          </td>
                          <td>
                            {it.status === 'parsing' || it.status === 'pending' ? (
                              <span style={{ color: '#64748b' }}>判定中…</span>
                            ) : (
                              <select value={it.kind || ''}
                                      disabled={running || it.status === 'done' || it.status === 'skipped'}
                                      onChange={e => patch(it.id, { kind: e.target.value })}
                                      style={{ fontSize: '0.85rem', padding: '0.15rem 0.3rem' }}>
                                {optionsFor(it).map(k => (
                                  <option key={k.value} value={k.value}>{k.label}</option>
                                ))}
                              </select>
                            )}
                            {p && it.kind !== p.meta.kind && (
                              <div style={{ fontSize: '0.8rem', color: '#b45309', marginTop: 2 }}>
                                自動判定は「{kindInfo(p.meta.kind).label}」
                              </div>
                            )}
                          </td>
                          <td>
                            <select value={it.classId || ''}
                                    disabled={running || it.status === 'done' || it.status === 'skipped'}
                                    onChange={e => patch(it.id, { classId: e.target.value, guessedFrom: null })}
                                    style={{ fontSize: '0.85rem', padding: '0.15rem 0.3rem',
                                             maxWidth: '11rem' }}>
                              {it.classId && !classList.some(c => String(c.id) === String(it.classId)) && (
                                <option value={String(it.classId)}>
                                  {(allClassList.find(c => String(c.id) === String(it.classId)) || {}).name || 'この授業'}
                                </option>
                              )}
                              {classList.map(c => (
                                <option key={c.id} value={String(c.id)}>{c.name}</option>
                              ))}
                            </select>
                            {it.guessedFrom && (
                              <div style={{ fontSize: '0.78rem', color: '#0369a1', marginTop: 2 }}>
                                フォルダ名から推測
                              </div>
                            )}
                          </td>
                          <td style={{ maxWidth: 260, overflow: 'hidden',
                                       textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                              title={p ? p.meta.title : ''}>
                            {p ? p.meta.title : (it.kind === 'attendance' ? '（出席記録）' : '—')}
                          </td>
                          <td style={{ whiteSpace: 'nowrap' }}>{p ? (p.meta.assignedDate || '—') : '—'}</td>
                          <td style={{ textAlign: 'right' }}>
                            {p ? p.summary.submitted + ' / ' + p.summary.total : '—'}
                          </td>
                          <td style={{ textAlign: 'right',
                                       color: p && p.summary.unmatched > 0 ? '#dc2626' : '#64748b' }}>
                            {p ? (p.summary.unmatched > 0 ? p.summary.unmatched : '—') : '—'}
                          </td>
                          <td style={{ whiteSpace: 'nowrap', color: stColor }}>
                            {stLabel}
                            {it.status === 'done' && it.result && (
                              <div style={{ fontSize: '0.8rem', color: '#64748b' }}>
                                {it.result.matched} 人
                              </div>
                            )}
                          </td>
                          <td style={{ whiteSpace: 'nowrap' }}>
                            {p && (
                              <button className="btn-secondary"
                                      style={{ padding: '0.2rem 0.5rem', fontSize: '0.8rem' }}
                                      onClick={() => patch(it.id, { expanded: !it.expanded })}>
                                {it.expanded ? '閉じる' : '中身'}
                              </button>
                            )}
                            {it.status === 'ready' && !running && (
                              <button className="btn-secondary"
                                      style={{ padding: '0.2rem 0.5rem', fontSize: '0.8rem',
                                               marginLeft: '0.3rem' }}
                                      onClick={() => patch(it.id, { status: 'skipped' })}>
                                除外
                              </button>
                            )}
                            {it.status === 'skipped' && (
                              <button className="btn-secondary"
                                      style={{ padding: '0.2rem 0.5rem', fontSize: '0.8rem' }}
                                      onClick={() => patch(it.id, { status: 'ready' })}>
                                戻す
                              </button>
                            )}
                            {it.status === 'error' && !running && (
                              <button className="btn-secondary"
                                      style={{ padding: '0.2rem 0.5rem', fontSize: '0.8rem' }}
                                      onClick={() => parseOne(it)}>
                                再試行
                              </button>
                            )}
                          </td>
                        </tr>

                        {it.error && (
                          <tr>
                            <td colSpan={9} style={{ background: '#fef2f2', color: '#991b1b',
                                                     fontSize: '0.85rem' }}>
                              {it.error}
                            </td>
                          </tr>
                        )}

                        {it.expanded && p && (
                          <tr>
                            <td colSpan={9} style={{ background: '#f8fafc' }}>
                              <div style={{ fontSize: '0.82rem', color: '#475569',
                                            marginBottom: '0.5rem' }}>
                                取り込み先：<strong>{p.class ? p.class.name : (className || 'この授業')}</strong>
                                {p.meta.maxScore != null && '　満点 ' + p.meta.maxScore}
                                {p.meta.questionCount > 0 && '　設問 ' + p.meta.questionCount}
                                {p.meta.freeFormCount > 0 && '　自由記述 ' + p.meta.freeFormCount}
                                {p.duplicate && (
                                  <span style={{ color: '#b45309', marginLeft: '0.6rem' }}>
                                    既に取り込み済み（{p.duplicate.score_count} 件）→ 上書きになります
                                  </span>
                                )}
                                {(p.sameFileImports || []).length > 0 && (
                                  <span style={{ color: '#b45309', marginLeft: '0.6rem' }}>
                                    同じ名前のファイルを {new Date(p.sameFileImports[0].importedAt).toLocaleDateString('ja-JP')} に取り込み済み
                                  </span>
                                )}
                              </div>
                              <div className="table-wrap"
                                   style={{ maxHeight: '30vh', overflow: 'auto',
                                            border: '1px solid #e5e7eb', borderRadius: 8 }}>
                                <table className="data-table" style={{ margin: 0 }}>
                                  <thead>
                                    <tr>
                                      <th>学籍番号</th><th>氏名</th><th>状態</th>
                                      <th style={{ textAlign: 'right' }}>得点</th>
                                      <th style={{ textAlign: 'right' }}>感想</th>
                                      <th style={{ textAlign: 'right' }}>発言</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {p.rows.map((r, i) => (
                                      <tr key={i} style={r.matchedUserId ? undefined : { background: '#fef2f2' }}>
                                        <td style={{ whiteSpace: 'nowrap' }}>
                                          {r.rawStudentId}
                                          {!r.matchedUserId && (
                                            <span style={{ color: '#dc2626', fontSize: '0.8rem',
                                                           marginLeft: '0.35rem' }}>突合できず</span>
                                          )}
                                        </td>
                                        <td style={{ whiteSpace: 'nowrap' }}>{r.matchedName || r.rawName}</td>
                                        <td style={{ whiteSpace: 'nowrap' }}>{r.status}</td>
                                        <td style={{ textAlign: 'right' }}>{r.score == null ? '—' : r.score}</td>
                                        <td style={{ textAlign: 'right' }}>
                                          {r.reflectionLen ? r.reflectionLen + '字' : '—'}
                                        </td>
                                        <td style={{ textAlign: 'right' }}>
                                          {r.speakCount == null ? '—' : r.speakCount}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
            )}
          </div>
        )}

        {/* ---- まとめて見る【2026-09-11】 ----
             見本の「出席×小テスト×提出物」と同じ。学籍番号を軸に、
             落としたファイルを列として横に並べる。
             サーバーには何も送っていない。保存をオフにしたまま確認できる。 */}
        {combined && cross.cols.length > 0 && (
          <div style={{ marginTop: '1rem' }}>
            <div style={{ fontSize: '0.85rem', color: '#4b5563', marginBottom: '0.4rem' }}>
              落とした {cross.cols.length} 枚を学籍番号で突き合わせています（{cross.rows.length} 人）。
              <strong>サーバーには保存していません。</strong>
              画面を離れると消えるので、残すなら上の「取り込む」を使ってください。
            </div>
            <div className="table-wrap" style={{ maxHeight: '60vh', overflow: 'auto',
                                                 border: '1px solid #e5e7eb', borderRadius: 8 }}>
              <table className="data-table" style={{ margin: 0 }}>
                <thead>
                  <tr>
                    <th style={{ position: 'sticky', left: 0, top: 0, zIndex: 3,
                                 background: '#f9fafb', whiteSpace: 'nowrap' }}>学籍番号</th>
                    <th style={{ position: 'sticky', left: 96, top: 0, zIndex: 3,
                                 background: '#f9fafb', whiteSpace: 'nowrap' }}>氏名</th>
                    {cross.cols.map(it => (
                      <th key={it.id} title={it.preview.meta.title}
                          style={{ position: 'sticky', top: 0, zIndex: 2, background: '#f9fafb',
                                   whiteSpace: 'nowrap', maxWidth: 130, overflow: 'hidden',
                                   textOverflow: 'ellipsis', textAlign: 'center' }}>
                        {it.preview.meta.title}
                        <div style={{ fontWeight: 400, color: '#6b7280', fontSize: '0.78rem' }}>
                          {kindInfo(it.kind).label}
                          {it.preview.meta.maxScore != null && '／' + it.preview.meta.maxScore + '点'}
                        </div>
                      </th>
                    ))}
                    <th style={{ position: 'sticky', top: 0, zIndex: 2, background: '#eef2ff',
                                 whiteSpace: 'nowrap', textAlign: 'right' }}>合計</th>
                  </tr>
                </thead>
                <tbody>
                  {cross.rows.map(row => (
                    <tr key={row.id} style={row.matched ? undefined : { background: '#fef2f2' }}>
                      <td style={{ position: 'sticky', left: 0, zIndex: 1, background: '#fff',
                                   whiteSpace: 'nowrap', minWidth: 96 }}>
                        {row.id}
                        {!row.matched && (
                          <span style={{ color: '#dc2626', fontSize: '0.74rem', marginLeft: '0.3rem' }}>
                            名簿外
                          </span>
                        )}
                      </td>
                      <td style={{ position: 'sticky', left: 96, zIndex: 1, background: '#fff',
                                   whiteSpace: 'nowrap', boxShadow: '2px 0 0 #e5e7eb' }}>
                        {row.name || '—'}
                      </td>
                      {cross.cols.map(it => {
                        const cell = crossCell(it, row.cells[it.id]);
                        return (
                          <td key={it.id} title={cell.title}
                              style={{ textAlign: 'center', whiteSpace: 'nowrap',
                                       color: cell.dim ? '#dc2626' : undefined }}>
                            {cell.text || '·'}
                          </td>
                        );
                      })}
                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap', background: '#f8fafc' }}>
                        {row.total.hasScore && (
                          <strong>{row.total.scoreSum}
                            {row.total.scoreMax > 0 && '/' + row.total.scoreMax}</strong>
                        )}
                        <div style={{ fontSize: '0.78rem', color: '#6b7280' }}>
                          提出 {row.total.subCount}/{cross.cols.length}
                          {row.total.refCount > 0 && '　感想 ' + row.total.refCount}
                          {row.total.speakSum > 0 && '　発言 ' + row.total.speakSum}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ---- 取り込み済み ---- */}
        <div style={{ marginTop: '1.2rem' }}>
          <div style={{ fontSize: '0.85rem', color: '#475569', fontWeight: 600,
                        marginBottom: '0.4rem' }}>
            取り込み済み
          </div>
          {listBusy ? (
            <p style={{ color: '#6b7280', fontSize: '0.88rem', margin: 0 }}>読み込み中…</p>
          ) : list.length === 0 ? (
            <p style={{ color: '#64748b', fontSize: '0.88rem', margin: 0 }}>
              まだ何も取り込まれていません。
            </p>
          ) : (
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th style={{ whiteSpace: 'nowrap' }}>種類</th>
                    <th>課題名</th>
                    <th style={{ whiteSpace: 'nowrap' }}>授業日</th>
                    <th style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>満点</th>
                    <th style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>提出</th>
                    <th style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>人数</th>
                    <th style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>突合できず</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map(a => {
                    const k = kindInfo(a.kind);
                    const un = Number(a.unmatched_count);
                    return (
                      <tr key={a.id}>
                        <td>
                          <span style={{ background: k.bg, color: k.fg, borderRadius: 999,
                                         padding: '0.1rem 0.55rem', fontSize: '0.8rem',
                                         whiteSpace: 'nowrap' }}>{k.label}</span>
                        </td>
                        <td style={{ maxWidth: 360, overflow: 'hidden',
                                     textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.title}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          {a.assigned_date ? String(a.assigned_date).slice(0, 10) : '—'}
                        </td>
                        <td style={{ textAlign: 'right' }}>{a.max_score == null ? '—' : a.max_score}</td>
                        <td style={{ textAlign: 'right' }}>{a.submitted_count}</td>
                        <td style={{ textAlign: 'right' }}>{a.row_count}</td>
                        <td style={{ textAlign: 'right', color: un > 0 ? '#dc2626' : '#64748b' }}>
                          {un > 0 ? un : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ---- ドラッグ中のオーバーレイ（共通の見た目）---- */}
      <PageDropOverlay
        show={dragging}
        title="ファイルをドロップして取り込む"
        note={`${className || 'この授業'} に取り込みます（小テスト・課題・出席CSV、フォルダごとでも可）`}
        kinds={['CSV', 'XLSX', 'フォルダ']}
      />
    </>
  );
}
